import { getCachedPushCid } from '@/plugins/uni-push';
import { request } from '@/utils/request/request';

// 登录/注册/退出接口统一附带推送 cid，供后端绑定/解绑别名、标签；
// H5/小程序或 cid 未就绪时为空字符串，后端按空值忽略即可

// 密码登录
export function loginByPassword(data: {
  mobile: string
  password: string
  mobileType: number
  isSeven?: string
}) {
  return request<API.LoginResult>({
    url: `/newapi/user/users/loginByPasswordV2`,
    method: 'post',
    data: {
      businessParam: {
        ...data,
        isSeven: data.isSeven || '4',
        cid: getCachedPushCid(), // 推送设备ID
      },
    },
  }, {
    notificationType: 'all',
    successMsg: '登录成功',
  });
}

// 子账号密码登录（账号格式：主账号手机号@子账号标识）
export function subUserLoginByPassword(data: {
  loginUserName: string
  password: string
}) {
  return request<API.LoginResult>({
    url: `/newapi/user/subUser/loginByPassword`,
    method: 'post',
    data: {
      ...data,
      isSeven: 4,
      cid: getCachedPushCid(), // 推送设备ID
    },
  }, {
    notificationType: 'all',
    successMsg: '登录成功',
  });
}

// 短信登录
export function loginByMessage(data: {
  mobile: string
  msgCode: string
  yz_code: string
  mobileType: number
}) {
  return request<API.LoginResult>({
    url: `/newapi/user/users/loginByCheckCode`,
    method: 'post',
    data: {
      ...data,
      cid: getCachedPushCid(), // 推送设备ID
    },
  }, {
    notificationType: 'all',
    successMsg: '登录成功',
  });
}

/**
 * 发送短信验证码
 * type 1：注册，2：忘记密码，3：短信登录，4：修改账号(手机号)，5：提现，6：编辑账号信息验证，9：注销
 */
export function sendMessageCode(data: {
  mobile: string
  mobileType: number
  type: number
  msgType?: number
  imageCode?: string
}) {
  const url = [5, 6, 9].includes(data.type) ? '/newapi/user/users/sendMsgCode' : '/newapi/user/users/sendMsgCodeNoLogin';

  return request<{ yz_code: string }>({
    url,
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '验证码已发送至手机',
  });
}

/**
 * 发送邮箱验证码
 * type 1：注册，2：忘记密码，3：短信登录，4：修改账号(手机号)，5：提现，6：编辑账号信息验证，9：注销
 */
export function sendEmailCode(data: {
  email: string
  type: number
  imageCode?: string
}) {
  const url = [5, 6, 9].includes(data.type) ? '/newapi/user/users/sendMsgCodeEmail' : '/newapi/user/users/sendMsgCodeEmailNoLogin';
  return request<{ yz_code: string }>({
    url,
    method: 'post',
    data,
  }, {
    notificationType: 'all',
    successMsg: '验证码已发送至邮箱',
  });
}

/**
 * 刷新token
 * @param refreshToken 刷新token
 */
export function refreshToken(refreshToken: string) {
  return request<API.LoginResult>({
    url: `/user/refreshToken`,
    method: 'post',
    data: {
      refreshToken,
    },
  });
}

// 退出登录（携带 cid 供后端解绑别名/标签，避免退出后继续收到定向推送）
export function logout() {
  return request({
    url: `/newapi/user/users/logout`,
    method: 'post',
    data: {
      cid: getCachedPushCid(), // 推送设备ID
    },
  }, {
    notificationType: 'none',
  });
}

/**
 * 获取微信登录凭证
 * @returns Promise 包含微信登录凭证(code)
 */
export function getWxCode() {
  return new Promise<UniApp.LoginRes>((resolve, reject) => {
    uni.login({
      provider: 'weixin',
      success: res => resolve(res),
      fail: err => reject(new Error(err)),
    });
  });
}

/**
 * 微信登录
 * @param params 微信登录参数，包含code
 * @returns Promise 包含登录结果
 */
export function wxLogin(data: { code: string }) {
  return request<API.LoginResult>({
    url: `/auth/wxLogin`,
    method: 'post',
    data: {
      ...data,
      cid: getCachedPushCid(), // 推送设备ID
    },
  });
}

/**
 * 用户注册
 * @param data 注册参数
 */
export function register(data: {
  mobile: string
  password: string
  msgCode: string
  yz_code: string
  mobileType: number
}) {
  return request<API.LoginResult>({
    url: `/newapi/user/users/register`,
    method: 'post',
    data: {
      ...data,
      cid: getCachedPushCid(), // 推送设备ID
    },
  }, {
    notificationType: 'all',
    successMsg: '注册成功',
  });
}

/**
 * 忘记密码
 * @param data 重置密码参数
 */
export function forgetPassword(data: {
  mobile: string
  password: string
  msgCode: string
  yz_code: string
  mobileType: number
}) {
  return request<API.LoginResult>({
    url: `/newapi/user/users/forgetPassword`,
    method: 'post',
    data,
  }, {
    notificationType: 'all',
  });
}
